/**
 * The seam between the web app and a native shell (Tauri on the desktop,
 * Capacitor on mobile).
 *
 * Both shells expose their APIs as globals inside the webview — Tauri as
 * `window.__TAURI__` (with `withGlobalTauri`), Capacitor as `window.Capacitor`.
 * Reading those globals rather than importing either SDK keeps both out of the
 * web bundle entirely: the browser build is byte-for-byte unaware that shells
 * exist, and the shells need no separate build of the app.
 *
 * What the app actually needs from a shell is small, and it is all here: a
 * folder it may write notes into, and the system share sheet. Everything else
 * markbird already does with web APIs.
 */

export type NativePlatform = 'tauri' | 'capacitor';

export interface NativeFile {
  /** Path relative to the vault root, slash-separated. */
  path: string;
  lastModified: number;
}

export interface NativeFiles {
  /** Every `.md` file under the vault root, recursively. */
  list(): Promise<NativeFile[]>;
  read(path: string): Promise<string>;
  /** Write, creating parent folders. Returns the file's modification time. */
  write(path: string, text: string): Promise<number>;
  remove(path: string): Promise<void>;
}

/** The operating system underneath the shell, as far as it will say. */
export type NativeOs = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';

export interface NativeShell {
  platform: NativePlatform;
  os: NativeOs;
  /** Where the vault lives, in words, for the settings screen. */
  vaultLabel: string;
  files: NativeFiles;
  /** The system share sheet, when the shell has one. */
  share?: (payload: { title?: string; text: string }) => Promise<boolean>;
  /**
   * Links the system hands a running app — a shared note opened from Messages,
   * say. The URL an app is *launched* with is just the address bar; this is for
   * every one after that, which a web app never sees.
   */
  onOpenUrl?: (handler: (url: string) => void) => void;
}

interface TauriFsEntry {
  name: string;
  isDirectory: boolean;
}

interface TauriGlobal {
  deepLink?: { onOpenUrl: (cb: (urls: string[]) => void) => unknown };
  fs?: {
    readDir: (path: string, options?: unknown) => Promise<TauriFsEntry[]>;
    readTextFile: (path: string, options?: unknown) => Promise<string>;
    writeTextFile: (path: string, text: string, options?: unknown) => Promise<void>;
    mkdir: (path: string, options?: unknown) => Promise<void>;
    remove: (path: string, options?: unknown) => Promise<void>;
    stat: (path: string, options?: unknown) => Promise<{ mtime?: number | string | Date | null }>;
    exists?: (path: string, options?: unknown) => Promise<boolean>;
    BaseDirectory?: Record<string, number>;
  };
}

interface CapacitorStat {
  mtime?: number;
}

interface CapacitorGlobal {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
  Plugins?: {
    App?: { addListener: (event: 'appUrlOpen', cb: (payload: { url: string }) => void) => unknown };
    Filesystem?: {
      readdir: (o: { path: string; directory: string }) => Promise<{
        files: { name: string; type: 'file' | 'directory'; mtime?: number }[];
      }>;
      readFile: (o: {
        path: string;
        directory: string;
        encoding?: string;
      }) => Promise<{ data: string }>;
      writeFile: (o: {
        path: string;
        directory: string;
        data: string;
        encoding?: string;
        recursive?: boolean;
      }) => Promise<unknown>;
      deleteFile: (o: { path: string; directory: string }) => Promise<unknown>;
      stat: (o: { path: string; directory: string }) => Promise<CapacitorStat>;
      mkdir?: (o: { path: string; directory: string; recursive?: boolean }) => Promise<unknown>;
    };
    Share?: { share: (o: { title?: string; text?: string }) => Promise<unknown> };
  };
}

interface ShellWindow {
  __TAURI__?: TauriGlobal;
  Capacitor?: CapacitorGlobal;
}

/** Which shell the app is running inside, or null in an ordinary browser. */
export function detectNativeShell(
  scope: ShellWindow = globalThis as ShellWindow,
): NativePlatform | null {
  if (scope.__TAURI__?.fs) return 'tauri';
  // Capacitor's global also exists in its web target, where the filesystem is
  // a browser shim and the vault should stay in the browser's own storage.
  if (scope.Capacitor?.Plugins?.Filesystem && scope.Capacitor.isNativePlatform?.()) {
    return 'capacitor';
  }
  return null;
}

/** The vault folder inside the app's own storage, where the shell can always write. */
const VAULT_DIR = 'markbird';
const MD = /\.md$/i;

function join(...parts: string[]): string {
  return parts.filter(Boolean).join('/');
}

function toMillis(value: number | string | Date | null | undefined): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/** Tauri does not name the platform without a plugin; the user agent does. */
function desktopOs(): NativeOs {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/Mac OS X|Macintosh/.test(ua)) return 'macos';
  if (/Windows/.test(ua)) return 'windows';
  if (/Linux|X11/.test(ua)) return 'linux';
  return 'unknown';
}

function capacitorOs(platform: string | undefined): NativeOs {
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'unknown';
}

function tauriShell(
  api: NonNullable<TauriGlobal['fs']>,
  deepLink: TauriGlobal['deepLink'],
): NativeShell {
  // AppData: the directory the platform gives the app for its own documents,
  // which on iOS is what survives storage pressure — unlike webview storage.
  const base = api.BaseDirectory?.AppData ?? 1;
  const options = { baseDir: base } as const;

  const walk = async (relative: string): Promise<NativeFile[]> => {
    const entries = await api
      .readDir(join(VAULT_DIR, relative) || VAULT_DIR, options)
      .catch(() => []);
    const out: NativeFile[] = [];
    for (const entry of entries) {
      if (entry.name.startsWith('.')) continue;
      const path = join(relative, entry.name);
      if (entry.isDirectory) out.push(...(await walk(path)));
      else if (MD.test(entry.name)) {
        const stat = await api.stat(join(VAULT_DIR, path), options).catch(() => ({ mtime: 0 }));
        out.push({ path, lastModified: toMillis(stat.mtime) });
      }
    }
    return out;
  };

  return {
    platform: 'tauri',
    os: desktopOs(),
    ...(deepLink
      ? {
          onOpenUrl: (handler: (url: string) => void) => {
            deepLink.onOpenUrl((urls) => {
              for (const url of urls) handler(url);
            });
          },
        }
      : {}),
    vaultLabel: `${VAULT_DIR} (app folder)`,
    files: {
      list: () => walk(''),
      read: (path) => api.readTextFile(join(VAULT_DIR, path), options),
      async write(path, text) {
        const slash = path.lastIndexOf('/');
        if (slash > 0) {
          await api
            .mkdir(join(VAULT_DIR, path.slice(0, slash)), { ...options, recursive: true })
            .catch(() => {});
        } else {
          await api.mkdir(VAULT_DIR, { ...options, recursive: true }).catch(() => {});
        }
        await api.writeTextFile(join(VAULT_DIR, path), text, options);
        const stat = await api.stat(join(VAULT_DIR, path), options).catch(() => ({ mtime: 0 }));
        return toMillis(stat.mtime) || Date.now();
      },
      async remove(path) {
        await api.remove(join(VAULT_DIR, path), options);
      },
    },
  };
}

function capacitorShell(capacitor: CapacitorGlobal): NativeShell {
  const fs = capacitor.Plugins!.Filesystem!;
  const share = capacitor.Plugins?.Share;
  const app = capacitor.Plugins?.App;
  // DATA is the app's private documents directory on both platforms.
  const directory = 'DATA';

  const walk = async (relative: string): Promise<NativeFile[]> => {
    const path = join(VAULT_DIR, relative) || VAULT_DIR;
    const listing = await fs.readdir({ path, directory }).catch(() => ({ files: [] }));
    const out: NativeFile[] = [];
    for (const entry of listing.files) {
      if (entry.name.startsWith('.')) continue;
      const next = join(relative, entry.name);
      if (entry.type === 'directory') out.push(...(await walk(next)));
      else if (MD.test(entry.name)) {
        const mtime =
          entry.mtime ??
          (
            await fs
              .stat({ path: join(VAULT_DIR, next), directory })
              .catch((): CapacitorStat => ({}))
          ).mtime ??
          0;
        out.push({ path: next, lastModified: mtime });
      }
    }
    return out;
  };

  return {
    platform: 'capacitor',
    // Capacitor knows which platform it is; a desktop shell has to be guessed.
    os: capacitorOs(capacitor.getPlatform?.()),
    vaultLabel: `${VAULT_DIR} (app documents)`,
    files: {
      list: () => walk(''),
      async read(path) {
        const { data } = await fs.readFile({
          path: join(VAULT_DIR, path),
          directory,
          encoding: 'utf8',
        });
        return data;
      },
      async write(path, text) {
        await fs.writeFile({
          path: join(VAULT_DIR, path),
          directory,
          data: text,
          encoding: 'utf8',
          // Capacitor creates missing parent folders only when asked.
          recursive: true,
        });
        const stat = await fs
          .stat({ path: join(VAULT_DIR, path), directory })
          .catch((): CapacitorStat => ({}));
        return stat.mtime ?? Date.now();
      },
      async remove(path) {
        await fs.deleteFile({ path: join(VAULT_DIR, path), directory });
      },
    },
    ...(app
      ? {
          onOpenUrl: (handler: (url: string) => void) => {
            app.addListener('appUrlOpen', ({ url }) => handler(url));
          },
        }
      : {}),
    ...(share
      ? {
          share: async (payload: { title?: string; text: string }) => {
            try {
              await share.share(payload);
              return true;
            } catch {
              return false;
            }
          },
        }
      : {}),
  };
}

/** The shell this app is running in, or null in an ordinary browser. */
export function nativeShell(scope: ShellWindow = globalThis as ShellWindow): NativeShell | null {
  const platform = detectNativeShell(scope);
  if (platform === 'tauri' && scope.__TAURI__?.fs) {
    return tauriShell(scope.__TAURI__.fs, scope.__TAURI__.deepLink);
  }
  if (platform === 'capacitor' && scope.Capacitor) return capacitorShell(scope.Capacitor);
  return null;
}

/**
 * Whether this is an Apple platform, where Sign in with Apple is not a choice:
 * offering Google or GitHub sign-in there requires offering Apple's too
 * (App Store guideline 4.8).
 */
export function isApplePlatform(scope: ShellWindow = globalThis as ShellWindow): boolean {
  const os = nativeShell(scope)?.os;
  return os === 'ios' || os === 'macos';
}
